import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Table, Tag } from '@voice/ui';
import { errorText, formatDuration, useDirectory } from '../components/common';
import { api } from '../lib/api';
import styles from './Pages.module.css';

interface Lot {
  id: string;
  name: string;
  startSlot: number;
  endSlot: number;
  timeoutSec: number;
  fallbackExtension?: string | null;
}
interface ParkingResponse {
  lots: Lot[];
  parked: { slot: number; lot: string; number: string; name: string | null; parkedBy: string; parkedMs: number }[];
}
interface Conference {
  id: string;
  name: string;
  number: string;
  pin: string | null;
  hasPin: boolean;
  maxMembers: number;
  members: { number: string; name: string | null; joinedMs: number }[];
}

export function ParkingConference() {
  return (
    <div className={styles.page}>
      <PageHeader title="Parken und Konferenzen" />
      <ParkingSection />
      <ConferenceSection />
    </div>
  );
}

// ---------------------------------------------------------------- Parken

function ParkingSection() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['parking'], queryFn: () => api<ParkingResponse>('/parking'), refetchInterval: 5000 });
  const [editing, setEditing] = useState<Lot | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['parking'] });
  const lots = query.data?.lots ?? [];
  return (
    <section className={styles.section} style={{ marginTop: 8 }}>
      <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
        <h2 className={styles.heading}>Parkplätze</h2>
        <Button variant="secondary" onClick={() => setEditing('new')}>
          Parkbereich anlegen
        </Button>
      </div>
      <p className={styles.note} style={{ marginBottom: 12 }}>
        Zum Parken leitest du ein Gespräch an die Parknummer weiter (z. B. 701). Jeder kann es danach wieder abholen, indem er diese Nummer wählt. Niemand holt es ab? Dann klingelt es nach Ablauf der Zeit wieder bei dem, der geparkt hat.
      </p>
      {query.isError && <Notice tone="error">Parkplätze konnten nicht geladen werden.</Notice>}
      {query.data && lots.length === 0 && <EmptyState>Noch kein Parkbereich.</EmptyState>}
      {lots.length > 0 && (
        <Table aria-label="Parkbereiche">
          <thead>
            <tr>
              <th>Name</th>
              <th>Nummern</th>
              <th>Rückruf nach</th>
              <th>Belegt</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lots.map((l) => {
              const parked = query.data!.parked.filter((p) => p.lot === l.name);
              return (
                <tr key={l.id}>
                  <th scope="row">{l.name}</th>
                  <td>
                    {l.startSlot}–{l.endSlot}
                  </td>
                  <td>{formatDuration(l.timeoutSec * 1000)} Min.</td>
                  <td>
                    {parked.length === 0 ? (
                      <span className={styles.muted}>frei</span>
                    ) : (
                      parked.map((p) => (
                        <div key={p.slot}>
                          <Tag tone="warn">{p.slot}</Tag> {p.name ?? p.number} · seit {formatDuration(p.parkedMs)}
                        </div>
                      ))
                    )}
                  </td>
                  <td>
                    <RowActions>
                      <Button variant="secondary" onClick={() => setEditing(l)}>
                        Bearbeiten
                      </Button>
                    </RowActions>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {editing && <LotForm lot={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onChanged={refresh} />}
    </section>
  );
}

function LotForm({ lot, onClose, onChanged }: { lot: Lot | null; onClose: () => void; onChanged: () => void }) {
  const [name, setName] = useState(lot?.name ?? 'Haus');
  const [start, setStart] = useState(String(lot?.startSlot ?? 701));
  const [end, setEnd] = useState(String(lot?.endSlot ?? 709));
  const [timeout, setTimeoutSec] = useState(String(lot?.timeoutSec ?? 120));
  const [fallback, setFallback] = useState(lot?.fallbackExtension ?? '');
  const body = () => ({ name, startSlot: Number(start), endSlot: Number(end), timeoutSec: Number(timeout), fallbackExtension: fallback || null });
  const save = useMutation({
    mutationFn: () => (lot ? api(`/parking/${lot.id}`, { method: 'PUT', body: body() }) : api('/parking', { method: 'POST', body: body() })),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/parking/${lot!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={lot ? `Parkbereich „${lot.name}“` : 'Parkbereich anlegen'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!name || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <div className={styles.row}>
          <Field label="Erste Parknummer" inputMode="numeric" value={start} onChange={(e) => setStart(e.target.value.replace(/\D/g, ''))} />
          <Field label="Letzte Parknummer" inputMode="numeric" value={end} onChange={(e) => setEnd(e.target.value.replace(/\D/g, ''))} />
        </div>
        <Field label="Nach wie vielen Sekunden klingelt es wieder?" inputMode="numeric" value={timeout} onChange={(e) => setTimeoutSec(e.target.value.replace(/\D/g, ''))} />
        <Field label="Ersatz-Nebenstelle (wenn der Parkende nicht erreichbar ist)" inputMode="numeric" value={fallback} onChange={(e) => setFallback(e.target.value.replace(/\D/g, ''))} placeholder="z. B. Empfang" />
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {lot && (
          <div>
            <Button variant="secondary" onClick={() => confirm('Parkbereich löschen?') && remove.mutate()}>
              Löschen
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Konferenzen

function ConferenceSection() {
  const qc = useQueryClient();
  const dir = useDirectory();
  void dir;
  const query = useQuery({ queryKey: ['conferences'], queryFn: () => api<{ conferences: Conference[] }>('/conferences'), refetchInterval: 5000 });
  const [editing, setEditing] = useState<Conference | 'new' | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['conferences'] }), qc.invalidateQueries({ queryKey: ['directory'] })]);
  const list = query.data?.conferences ?? [];
  return (
    <section className={styles.section}>
      <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
        <h2 className={styles.heading}>Konferenzräume</h2>
        <Button variant="secondary" onClick={() => setEditing('new')}>
          Raum anlegen
        </Button>
      </div>
      {query.isError && <Notice tone="error">Konferenzräume konnten nicht geladen werden.</Notice>}
      {query.data && list.length === 0 && <EmptyState>Noch kein Konferenzraum.</EmptyState>}
      {list.length > 0 && (
        <Table aria-label="Konferenzräume">
          <thead>
            <tr>
              <th>Name</th>
              <th>Nummer</th>
              <th>PIN</th>
              <th>Teilnehmer</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id}>
                <th scope="row">{c.name}</th>
                <td>{c.number}</td>
                <td>{c.hasPin ? <span className={styles.mono}>{c.pin}</span> : <span className={styles.muted}>keine</span>}</td>
                <td>
                  {c.members.length} / {c.maxMembers}
                  {c.members.length > 0 && <div className={styles.muted}>{c.members.map((m) => m.name ?? m.number).join(', ')}</div>}
                </td>
                <td>
                  <RowActions>
                    <Button variant="secondary" onClick={() => setEditing(c)}>
                      Bearbeiten
                    </Button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing && <ConferenceForm room={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onChanged={refresh} />}
    </section>
  );
}

function ConferenceForm({ room, onClose, onChanged }: { room: Conference | null; onClose: () => void; onChanged: () => void }) {
  const [name, setName] = useState(room?.name ?? '');
  const [number, setNumber] = useState(room?.number ?? '');
  const [pin, setPin] = useState(room?.pin ?? '');
  const [max, setMax] = useState(String(room?.maxMembers ?? 20));
  const body = () => ({ name, number, pin: pin || null, maxMembers: Number(max) || 20 });
  const save = useMutation({
    mutationFn: () => (room ? api(`/conferences/${room.id}`, { method: 'PUT', body: body() }) : api('/conferences', { method: 'POST', body: body() })),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/conferences/${room!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={room ? `Konferenzraum „${room.name}“` : 'Konferenzraum anlegen'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!name || !number || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <div className={styles.row}>
          <Field label="Interne Rufnummer" inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value.replace(/\D/g, ''))} placeholder="800" />
          <Field label="PIN (leer = ohne)" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
        </div>
        <Field label="Höchstens Teilnehmer" inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))} />
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {room && (
          <div>
            <Button variant="secondary" onClick={() => confirm('Raum löschen?') && remove.mutate()}>
              Löschen
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
